import FirebaseService from '@/services/FirebaseService'
import { collection, DocumentData, FirestoreDataConverter, getDocs, limit, orderBy, query, getCountFromServer } from 'firebase/firestore'

export const PROPOSAL_STATUS = {
  DRAFT: 'draft', // created
  AWAITING_ACCEPTANCE: 'awaiting_acceptance', // emailed to customer
  ACCEPTED: 'accepted', // customer accepted
  DECLINED: 'declined', // customer declined
  ARCHIVED: 'archived'
}

const getLastContractId = async () => {
  const db = await FirebaseService.firestore()
  const collectionRef = collection(db, 'proposals')
  const queryRef = query(collectionRef, orderBy('contractId', 'desc'), limit(1))
  const querySnapshot = await getDocs(queryRef)

  if (querySnapshot.empty) return '1000'

  const proposal = Object.assign({}, querySnapshot.docs[0].data())
  return proposal.contractId ? proposal.contractId.substring(4) : '1000'
}

export class Proposal {
  id: string
  customerId: string
  preparerId: string
  budgetId: string
  projectId: string
  contractId: string
  status: string
  contractDate: Date
  contractValidDate: Date
  estimatedStartDate: Date
  estimatedEndDate: Date
  paymentTerms: string
  depositAmount: number
  termsConditions: string
  brandSchedule: string
  lineItems: string[]
  signature: {
    name: string
    email: string
    timestamp: Date
    consent: boolean
    ipAddress: string
    signature: string
    signatureImage: string
    date: Date
  }

  viewCount: number
  lastViewed: Date
  lastNotified: Date
  notifyRealtor: boolean
  createdAt: Date
  updatedAt: Date
  contractTotal?: number
  changeOrderTotal?: number
  approvedChangeOrderTotal?: number

  constructor () {
    this.customerId = ''
    this.preparerId = ''
    this.budgetId = ''
    this.projectId = ''
    this.contractId = ''
    this.viewCount = 0
    this.lastViewed = null
    this.lastNotified = null
    this.notifyRealtor = false
    this.status = PROPOSAL_STATUS.DRAFT
    this.contractDate = new Date()
    this.contractValidDate = new Date()
    this.estimatedStartDate = null
    this.estimatedEndDate = null
    this.paymentTerms = ''
    this.depositAmount = 0
    this.termsConditions = ''
    this.brandSchedule = ''
    this.lineItems = []
    this.createdAt = new Date()
    this.updatedAt = new Date()
  }
}

export const generateContractId = async () => {
  const lastContractId = await getLastContractId()
  const label = 'PP'
  const year = new Date().getFullYear().toString().substring(2)
  const counter = parseInt(lastContractId)
  return `${label}${year}${counter + 1}`
}

export const generateChangeOrderId = async (proposalId: string) => {
  const db = await FirebaseService.firestore()
  const collectionRef = collection(db, 'proposals', proposalId, 'changeOrders')
  const querySnapshot = await getCountFromServer(collectionRef)

  if (querySnapshot.data().count <= 0) return '1'

  return querySnapshot.data().count + 1 + ''
}

export const proposalConverter: FirestoreDataConverter<Proposal, DocumentData> = {
  toFirestore: (proposal: Proposal) => {
    proposal.contractDate = new Date(proposal.contractDate)
    proposal.contractValidDate = new Date(proposal.contractValidDate)
    proposal.createdAt = proposal.createdAt ? new Date(proposal.createdAt) : new Date()
    proposal.updatedAt = new Date()
    return Object.assign({}, JSON.parse(JSON.stringify(proposal)))
  },

  fromFirestore: (snapshot, options) => {
    const data = snapshot.data(options)
    data.contractDate = (typeof data.contractDate === 'object') ? data.contractDate.toDate() : data.contractDate
    data.contractValidDate = (typeof data.contractValidDate === 'object') ? data.contractValidDate.toDate() : data.contractValidDate
    if (data.lastViewed) data.lastViewed = (typeof data.lastViewed === 'object') ? data.lastViewed.toDate() : data.lastViewed
    if (data.lastNotified) data.lastNotified = (typeof data.lastNotified === 'object') ? data.lastNotified.toDate() : data.lastNotified
    data.createdAt = (typeof data.createdAt === 'object') ? data.createdAt.toDate() : data.createdAt
    data.updatedAt = (typeof data.updatedAt === 'object') ? data.updatedAt.toDate() : data.updatedAt
    return Object.assign(new Proposal(), JSON.parse(JSON.stringify(data))) as Proposal
  }
}
